import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import dayjs from "dayjs";
import { useParams, Link } from "react-router-dom";
import AppDataGrid from "../../components/AppDataGrid";
import StatusNotesPanel from "../../components/StatusNotesPanel";
import { BASE_URL } from "../../utils";

const formatMoney = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "-";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = dayjs(value);
  return d.isValid() ? d.format("MM/DD/YYYY") : "-";
};

const formatBbl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";
  if (raw.includes("-")) return raw;
  if (raw.length === 10) return `${raw[0]}-${raw.slice(1, 6)}-${raw.slice(6, 10)}`;
  return raw;
};

const truncateChars = (value, maxChars = 50) => {
  if (value == null) return "-";
  const str = String(value).trim();
  if (!str) return "-";
  return str.length > maxChars ? `${str.slice(0, maxChars)}...` : str;
};

const normalizeDisplayValue = (value) => {
  if (value == null || value === "") return "-";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => normalizeDisplayValue(item)).filter(Boolean).join(", ") || "-";
  }
  if (typeof value === "object") {
    if (value instanceof Date) {
      return dayjs(value).isValid() ? dayjs(value).format("MM/DD/YYYY") : "-";
    }
    if (value.type === "Buffer" && Array.isArray(value.data)) {
      try {
        return new TextDecoder().decode(new Uint8Array(value.data));
      } catch {
        return String(value.data.join(", "));
      }
    }
    if (typeof value.data === "string") return value.data;
    if (typeof value.text === "string") return value.text;
    if (typeof value.value === "string") return value.value;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const ownerTypeClass = (value) => {
  const v = String(value || "").toLowerCase();
  if (v.includes("individual")) return "inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700";
  if (v.includes("estate") || v.includes("trust")) return "inline-flex rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-700";
  if (v.includes("fund") || v.includes("partnership")) return "inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700";
  if (v.includes("llc") || v.includes("corporation")) return "inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700";
  if (v.includes("bank")) return "inline-flex rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold text-cyan-700";
  if (v.includes("government")) return "inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700";
  return "inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700";
};

const sourceClass = (value) => {
  const v = String(value || "").toUpperCase();
  if (v === "DOB") return "inline-flex rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700";
  if (v === "ECB") return "inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700";
  if (v === "DOF") return "inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700";
  if (v === "HPD") return "inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700";
  if (v === "VAC") return "inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700";
  return "inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700";
};

const leadTierClass = (value) => {
  const v = String(value || "").toUpperCase();
  if (v === "NUCLEAR") return "inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700";
  if (v === "HOT") return "inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700";
  return "inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700";
};

const InfoRow = ({ label, value, className = "" }) => (
  <div className={`flex flex-col gap-0.5 min-w-0 ${className}`}>
    <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
      {label}
    </div>
    <div className="text-[13px] leading-5 text-slate-800 break-words">
      {normalizeDisplayValue(value)}
    </div>
  </div>
);

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
        {label}
      </div>
      <div className="text-base font-semibold text-slate-900">{value ?? 0}</div>
    </div>
  </div>
);

const SectionCard = ({ title, subtitle, children, rightNode }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <div className="text-[12px] font-semibold text-slate-900">{title}</div>
        {subtitle ? <div className="mt-0.5 text-[11px] text-slate-500">{subtitle}</div> : null}
      </div>
      {rightNode ? <div className="shrink-0">{rightNode}</div> : null}
    </div>
    {children}
  </div>
);

const InlineTooltipText = ({ value, shortLimit = 60 }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 420 });

  const raw = value == null ? "" : String(value).trim();
  if (!raw) return <span>-</span>;

  const shortText = raw.length > shortLimit ? `${raw.slice(0, shortLimit)}...` : raw;
  const needsMore = raw.length > shortLimit;

  const updatePosition = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const desiredWidth = 420;
    const left = Math.min(
      window.innerWidth - desiredWidth - 16,
      Math.max(16, rect.left)
    );
    const top = rect.bottom + 8;
    setPos({ top, left, width: desiredWidth });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onDocClick = (e) => {
      if (
        anchorRef.current?.contains(e.target) ||
        tooltipRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    document.addEventListener("mousedown", onDocClick);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [open]);

  return (
    <span className="inline-flex items-center gap-2 min-w-0">
      <span className="truncate">{shortText}</span>
      {needsMore && (
        <>
          <button
            ref={anchorRef}
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-[11px] font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            View more
          </button>

          {open &&
            ReactDOM.createPortal(
              <div
                ref={tooltipRef}
                className="fixed z-[9999] rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-2xl"
                style={{
                  top: `${pos.top}px`,
                  left: `${pos.left}px`,
                  width: `${pos.width}px`,
                  maxWidth: "calc(100vw - 32px)",
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-[12px] leading-5 text-slate-700 whitespace-normal break-words">
                    {raw}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => setOpen(false)}
                    aria-label="Close tooltip"
                  >
                    ?
                  </button>
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </span>
  );
};

const DescriptionCell = ({ value }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 520 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const raw = value == null ? "" : String(value).trim();
  if (!raw) return <span>-</span>;

  const shortText = truncateChars(raw, 34);
  const needsMore = raw.length > 34;

  const updatePosition = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const desiredWidth = 520;
    const left = Math.min(
      window.innerWidth - desiredWidth - 16,
      Math.max(16, rect.right - desiredWidth)
    );
    setPos({
      top: rect.bottom + 8,
      left,
      width: desiredWidth,
    });
  };

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    const onDocClick = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    document.addEventListener("mousedown", onDocClick);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [open]);

  return (
    <div className="flex items-center gap-2 min-w-0 w-full">
      <span className="truncate text-[13px] text-slate-700">{shortText}</span>

      {needsMore && (
        <>
          <button
            type="button"
            className="shrink-0 text-[11px] font-medium text-blue-600 hover:text-blue-700 hover:underline"
            onClick={() => setOpen((v) => !v)}
            ref={btnRef}
          >
            More
          </button>

          {open &&
            ReactDOM.createPortal(
              <div
                ref={popRef}
                className="fixed z-[9999] rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-2xl"
                style={{
                  top: `${pos.top}px`,
                  left: `${pos.left}px`,
                  width: `${pos.width}px`,
                  maxWidth: "calc(100vw - 32px)",
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-[12px] leading-5 text-slate-700 whitespace-normal break-words">
                    {raw}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => setOpen(false)}
                    aria-label="Close description"
                  >
                    ?
                  </button>
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
};

const DetailSection = ({ title, children, spanTwo = false }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-slate-50/80 p-4 ${
      spanTwo ? "md:col-span-2" : ""
    }`}
  >
    <div className="mb-3 border-b border-slate-200 pb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-600">
      {title}
    </div>
    {children}
  </div>
);

export default function ViolationsV2Detail() {
  const { bbl } = useParams();
  const gridRef = useRef(null);

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1,
  });
  const [sortField, setSortField] = useState("issue_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [ownerDraft, setOwnerDraft] = useState({
    owner_name: "",
    owner_address: "",
    owner_city: "",
    owner_state: "",
    owner_zip: "",
    email1: "",
    phone_no1: "",
  });
  const [savingOwner, setSavingOwner] = useState(false);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/violations-v2/${encodeURIComponent(bbl)}/summary`
        );
        setSummary(res.data);
        const row = res.data || {};
        setOwnerDraft({
          owner_name: row.owner_name || "",
          owner_address: row.owner_address || "",
          owner_city: row.owner_city || "",
          owner_state: row.owner_state || "",
          owner_zip: row.owner_zip || "",
          email1: row.email1 || "",
          phone_no1: row.phone_no1 || "",
        });
      } catch (err) {
        console.error("V2 detail summary error", err);
        setError("Unable to load property summary.");
      }
    };

    fetchSummary();
  }, [bbl]);

  useEffect(() => {
    const fetchViolations = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await axios.get(
          `${BASE_URL}/api/violations-v2/${encodeURIComponent(bbl)}/violations`,
          {
            params: {
              page: pagination.page,
              limit: pagination.limit,
              sortField,
              sortDirection,
            },
          }
        );

        setRows(res.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          ...(res.data?.pagination || {}),
        }));
      } catch (err) {
        console.error("V2 detail violations error", err);
        setRows([]);
        setPagination((p) => ({ ...p, total: 0, totalPages: 1 }));
        setError("Unable to load violations list.");
      } finally {
        setLoading(false);
      }
    };

    fetchViolations();
  }, [bbl, pagination.page, pagination.limit, sortField, sortDirection]);

  const address = summary?.address || "-";
  const formattedBbl = formatBbl(summary?.bbl || bbl);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Violation #",
        field: "violation_number",
        width: 130,
        minWidth: 120,
        sortable: true,
        filter: false,
      },
      {
        headerName: "Source",
        field: "source",
        width: 100,
        minWidth: 90,
        sortable: true,
        filter: false,
      },
      {
        headerName: "Type",
        field: "violation_type",
        width: 120,
        minWidth: 110,
        filter: false,
      },
      {
        headerName: "Severity",
        field: "severity",
        width: 110,
        minWidth: 100,
        filter: false,
      },
      {
        headerName: "Description",
        field: "description",
        flex: 1,
        minWidth: 320,
        filter: false,
        cellStyle: {
          overflow: "hidden",
          whiteSpace: "nowrap",
          textAlign: "left",
        },
        cellClass: "text-left",
        cellRenderer: (p) => <DescriptionCell value={p.value} />,
      },
      {
        headerName: "Issue Date",
        field: "issue_date",
        width: 120,
        minWidth: 110,
        sortable: true,
        filter: false,
        valueFormatter: (p) => formatDate(p.value),
      },
      {
        headerName: "Days Open",
        field: "days_open",
        width: 110,
        minWidth: 100,
        filter: false,
      },
      {
        headerName: "Balance",
        field: "balance_due",
        width: 120,
        minWidth: 110,
        sortable: true,
        filter: false,
        valueFormatter: (p) => formatMoney(p.value),
      },
      {
        headerName: "Priority",
        field: "is_priority_violation",
        width: 100,
        minWidth: 90,
        filter: false,
        valueFormatter: (p) => (p.value ? "Yes" : "-"),
      },
      {
        headerName: "View",
        field: "view",
        width: 96,
        minWidth: 86,
        sortable: false,
        filter: false,
        cellRenderer: (params) => (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            onClick={() => setSelected(params.data)}
          >
            Details
          </button>
        ),
      },
    ],
    []
  );

  const onGridReady = (params) => {
    gridRef.current = params.api;
  };

  const onSortChanged = (params) => {
    const model = params.api.getSortModel ? params.api.getSortModel() : [];
    if (!model || model.length === 0) return;

    const next = model[0];
    if (!next?.colId || !next?.sort) return;

    setSortField(next.colId);
    setSortDirection(next.sort);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const saveOwnerInfo = async () => {
    if (!bbl) return;
    setSavingOwner(true);
    try {
      const res = await axios.put(`${BASE_URL}/api/violations-v2/${encodeURIComponent(bbl)}/owner-info`, ownerDraft);
      const updated = res.data?.data || null;
      if (updated) {
        setSummary(updated);
        setOwnerDraft({
          owner_name: updated.owner_name || "",
          owner_address: updated.owner_address || "",
          owner_city: updated.owner_city || "",
          owner_state: updated.owner_state || "",
          owner_zip: updated.owner_zip || "",
          email1: updated.email1 || "",
          phone_no1: updated.phone_no1 || "",
        });
      } else {
        const refreshed = await axios.get(`${BASE_URL}/api/violations-v2/${encodeURIComponent(bbl)}/summary`);
        setSummary(refreshed.data);
      }
    } catch (err) {
      console.error("V2 owner update error", err);
      setError(err?.response?.data?.message || "Unable to save owner info.");
    } finally {
      setSavingOwner(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-3 md:px-5 md:py-5">
      <div className="mx-auto flex w-full max-w-[1800px] flex-col gap-4">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="bg-gradient-to-r from-indigo-50 via-sky-50 to-white px-4 py-4 md:px-6 md:py-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 via-blue-500 to-sky-500 text-sm font-bold text-white shadow-lg">
                  V2
                </div>

                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Web Platform
                  </div>
                  <h1 className="mt-0.5 text-[22px] font-bold leading-7 text-slate-900">
                    Property Detail
                  </h1>
                  <div className="mt-1 text-[13px] text-slate-600">
                    BBL: <span className="font-semibold text-slate-800">{formattedBbl}</span>
                  </div>
                </div>
              </div>

              <Link
                to="/violations-v2/properties"
                className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                target="_blank"
                rel="noreferrer"
              >
                Back to properties
              </Link>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <SectionCard title="Property Summary" subtitle="Core property information">
            <div className="mb-3 text-[15px] font-semibold leading-6 text-slate-900">
              <InlineTooltipText value={address} shortLimit={72} />
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <InfoRow label="Borough" value={summary?.borough_name} />
              <InfoRow label="Block" value={summary?.block} />
              <InfoRow label="Lot" value={summary?.lot} />
              <InfoRow label="Units" value={summary?.units ?? 0} />
              <InfoRow label="Year Built" value={summary?.pluto_year_built} />
              <InfoRow label="Bldg Class" value={summary?.pluto_bldg_class} />
              <InfoRow label="Assessed" value={formatMoney(summary?.pluto_assessed_total)} />
              <InfoRow label="Zoning" value={summary?.pluto_zoning} />
            </div>
          </SectionCard>

          <SectionCard
            title="Owner Info"
            subtitle="Ownership and contact details"
            rightNode={
              summary?.hpd_building_id ? (
                <a
                  href={`https://hpdonline.nyc.gov/hpdonline/building/${summary.hpd_building_id}/overview`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-9 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-3 text-[12px] font-semibold text-blue-700 transition hover:bg-blue-100"
                >
                  HPD Online
                </a>
              ) : null
            }
          >
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="text-[15px] font-semibold leading-6 text-slate-900">
                {summary?.owner_name || "-"}
              </div>
              {summary?.owner_type ? <span className={ownerTypeClass(summary.owner_type)}>{summary.owner_type}</span> : null}
            </div>

            <div className="mb-3 text-[12px] leading-5 text-slate-500">
              {summary?.owner_address || "-"}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Owner Name</span>
                <input
                  value={ownerDraft.owner_name}
                  onChange={(e) => setOwnerDraft((p) => ({ ...p, owner_name: e.target.value }))}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Owner Address</span>
                <input
                  value={ownerDraft.owner_address}
                  onChange={(e) => setOwnerDraft((p) => ({ ...p, owner_address: e.target.value }))}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Email</span>
                <input
                  value={ownerDraft.email1}
                  onChange={(e) => setOwnerDraft((p) => ({ ...p, email1: e.target.value }))}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Phone</span>
                <input
                  value={ownerDraft.phone_no1}
                  onChange={(e) => setOwnerDraft((p) => ({ ...p, phone_no1: e.target.value }))}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Owner City</span>
                <input
                  value={ownerDraft.owner_city}
                  onChange={(e) => setOwnerDraft((p) => ({ ...p, owner_city: e.target.value }))}
                  className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Owner State / Zip</span>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={ownerDraft.owner_state}
                    onChange={(e) => setOwnerDraft((p) => ({ ...p, owner_state: e.target.value }))}
                    className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                    placeholder="State"
                  />
                  <input
                    value={ownerDraft.owner_zip}
                    onChange={(e) => setOwnerDraft((p) => ({ ...p, owner_zip: e.target.value }))}
                    className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[13px] text-slate-800 outline-none transition focus:border-slate-300"
                    placeholder="Zip"
                  />
                </div>
              </label>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                Source: {normalizeDisplayValue(summary?.owner_source)}
              </div>
              {summary?.source_summary ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                  {normalizeDisplayValue(summary.source_summary)}
                </div>
              ) : null}
              <button
                type="button"
                onClick={saveOwnerInfo}
                disabled={savingOwner}
                className="ml-auto inline-flex h-9 items-center justify-center rounded-xl bg-blue-600 px-4 text-[12px] font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {savingOwner ? "Saving..." : "Save Owner Info"}
              </button>
            </div>
          </SectionCard>

          <SectionCard title="Totals" subtitle="High-level metrics">
            <div className="mb-1 text-[15px] font-semibold leading-6 text-slate-900">
              {summary?.total_violations || 0} violations
            </div>
            <div className="mb-3 text-[12px] text-slate-500">
              Balance due: {formatMoney(summary?.total_balance_due)}
            </div>

            <div className="flex flex-wrap gap-2">
              <span className={leadTierClass(summary?.lead_tier || "WARM")}>{summary?.lead_tier || "WARM"}</span>
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                Lead Score: {summary?.lead_score || 0}
              </span>
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                Latest Issue: {formatDate(summary?.latest_issue_date)}
              </span>
            </div>
          </SectionCard>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <StatCard label="DOB" value={summary?.dob_count} />
          <StatCard label="ECB" value={summary?.ecb_count} />
          <StatCard label="HPD" value={summary?.hpd_count} />
          <StatCard label="DOF / Tax Liens" value={summary?.dof_count} />
          <StatCard label="Vacate" value={summary?.vac_count} />
          <StatCard label="Priority" value={summary?.priority_count} />
        </div>

        <SectionCard title="Status & Notes" subtitle="Track workflow updates">
          <StatusNotesPanel entityId={bbl} apiBase="/api/violations-v2" />
        </SectionCard>

        <SectionCard
          title="Violations"
          subtitle="Sortable and filterable records"
          rightNode={
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] font-medium text-slate-600">
              {pagination.total} records
            </div>
          }
        >
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <AppDataGrid
              gridRef={gridRef}
              className="w-full ag-v2-grid"
              rowData={rows}
              columnDefs={columnDefs}
              loading={loading}
              height={"calc(100vh - 360px)"}
              minWidth={0}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: false,
                minWidth: 90,
                cellClass: "text-[13px] text-center",
                headerClass: "text-center",
                cellStyle: {
                  userSelect: "text",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
              rowHeight={44}
              headerHeight={40}
              onGridReady={onGridReady}
              onSortChanged={onSortChanged}
              alwaysShowVerticalScroll={true}
              alwaysShowHorizontalScroll={true}
              overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading data...</span>'
              overlayNoRowsTemplate='<span class="ag-overlay-loading-center">No violations found.</span>'
            />
          </div>

          <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: 1 }))}
                className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                First
              </button>

              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() =>
                  setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))
                }
                className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Prev
              </button>

              <div className="px-2 text-[13px] font-medium text-slate-700">
                Page {pagination.page} of {pagination.totalPages}
              </div>

              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() =>
                  setPagination((p) => ({
                    ...p,
                    page: Math.min(p.totalPages, p.page + 1),
                  }))
                }
                className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>

              <button
                type="button"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.totalPages }))}
                className="inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Last
              </button>
            </div>

            <div className="flex items-center gap-2 self-start md:self-auto">
              <label className="text-[12px] font-medium text-slate-600">Rows per page</label>
              <select
                value={pagination.limit}
                onChange={(e) =>
                  setPagination((p) => ({
                    ...p,
                    limit: Number(e.target.value),
                    page: 1,
                  }))
                }
                className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 outline-none transition focus:border-slate-300"
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </SectionCard>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[2px]">
          <div className="flex max-h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Violation
                  </div>
                  <div className="mt-0.5 text-[20px] font-bold text-slate-900">
                    Violation Detail
                  </div>
                  <div className="mt-1 text-[12px] text-slate-500">
                    #{selected.violation_number || "-"} � {selected.source || "-"}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.owner_type ? <span className={ownerTypeClass(selected.owner_type)}>{selected.owner_type}</span> : null}
                    {selected.source ? <span className={sourceClass(selected.source)}>{selected.source}</span> : null}
                  </div>
                </div>

                <button
                  type="button"
                  className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  onClick={() => setSelected(null)}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="overflow-auto px-5 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DetailSection title="Violation Info">
                  <div className="grid grid-cols-1 gap-3">
                    <InfoRow label="Violation #" value={selected.violation_number} />
                    <InfoRow label="Source" value={selected.source} />
                    <InfoRow label="Type" value={selected.violation_type} />
                    <InfoRow label="Type Code" value={selected.violation_type_code} />
                    <InfoRow label="Category" value={selected.violation_category} />
                    <InfoRow label="Severity" value={selected.severity} />
                    <InfoRow label="Issue Date" value={formatDate(selected.issue_date)} />
                    <InfoRow label="Days Open" value={selected.days_open} />
                    <InfoRow label="Status" value={selected.current_status} />
                    <InfoRow label="ECB Status" value={selected.ecb_violation_status} />
                    <InfoRow label="Description" value={selected.description} />
                  </div>
                </DetailSection>

                <DetailSection title="Financials">
                  <div className="grid grid-cols-1 gap-3">
                    <InfoRow label="Balance Due" value={formatMoney(selected.balance_due)} />
                    <InfoRow label="Penalty Imposed" value={formatMoney(selected.penalty_imposed)} />
                    <InfoRow label="Amount Paid" value={formatMoney(selected.amount_paid)} />
                    <InfoRow label="Certification" value={selected.certification_status} />
                    <InfoRow label="Priority" value={selected.is_priority_violation ? "Yes" : "No"} />
                    <InfoRow label="Overdue" value={selected.is_overdue ? "Yes" : "No"} />
                  </div>
                </DetailSection>

                <DetailSection title="Hearing / Disposition">
                  <div className="grid grid-cols-1 gap-3">
                    <InfoRow label="Hearing Date" value={formatDate(selected.hearing_date)} />
                    <InfoRow label="Hearing Status" value={selected.hearing_status} />
                    <InfoRow label="Disposition Date" value={formatDate(selected.disposition_date)} />
                    <InfoRow label="Disposition" value={selected.disposition_comments} />
                    <InfoRow label="Order #" value={selected.order_number} />
                    <InfoRow label="NOV Type" value={selected.nov_type} />
                    <InfoRow label="NOV Issued" value={formatDate(selected.nov_issued_date)} />
                    <InfoRow label="Correct By" value={formatDate(selected.original_correct_by_date)} />
                    <InfoRow label="Certify By" value={formatDate(selected.original_certify_by_date)} />
                  </div>
                </DetailSection>

                <DetailSection title="HPD-Specific">
                  <div className="grid grid-cols-1 gap-3">
                    <InfoRow label="HPD Status" value={selected.hpd_current_status} />
                    <InfoRow label="HPD Status Date" value={formatDate(selected.hpd_current_status_date)} />
                    <InfoRow label="Rent Impairing" value={selected.hpd_rent_impairing} />
                    <InfoRow label="Inspection Date" value={formatDate(selected.hpd_inspection_date)} />
                    <InfoRow label="Approved Date" value={formatDate(selected.hpd_approved_date)} />
                  </div>
                </DetailSection>

                <DetailSection title="Location" spanTwo>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
                    <InfoRow label="Apartment" value={selected.apartment} />
                    <InfoRow label="Story" value={selected.story} />
                    <InfoRow label="House #" value={selected.house_number} />
                    <InfoRow label="Street" value={selected.street} />
                    <InfoRow label="City" value={selected.city} />
                    <InfoRow label="Zip" value={selected.zip} />
                    <InfoRow label="Borough" value={selected.borough_name} />
                    <InfoRow label="BIN" value={selected.bin} />
                    <InfoRow label="Lat" value={selected.latitude} />
                    <InfoRow label="Lng" value={selected.longitude} />
                    <InfoRow label="NTA" value={selected.nta} />
                  </div>
                </DetailSection>

                <DetailSection title="Property / Owner" spanTwo>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2">
                    <InfoRow label="Owner" value={selected.owner_name} />
                    <InfoRow label="Owner Type" value={selected.owner_type} />
                    <InfoRow label="Owner Address" value={selected.owner_address} />
                    <InfoRow label="Owner City" value={selected.owner_city} />
                    <InfoRow label="Owner State" value={selected.owner_state} />
                    <InfoRow label="Owner Zip" value={selected.owner_zip} />
                    <InfoRow label="Owner Source" value={selected.owner_source} />
                    <InfoRow label="ACRIS Doc" value={selected.acris_document_id} />
                    <InfoRow label="ACRIS Date" value={formatDate(selected.acris_filing_date)} />
                    <InfoRow label="HPD Building ID" value={selected.hpd_building_id} />
                    <InfoRow label="Year Built" value={selected.pluto_year_built} />
                    <InfoRow label="Bldg Class" value={selected.pluto_bldg_class} />
                    <InfoRow label="Assessed" value={formatMoney(selected.pluto_assessed_total)} />
                    <InfoRow label="Lot Area" value={selected.pluto_lot_area} />
                    <InfoRow label="Bldg Area" value={selected.pluto_bldg_area} />
                    <InfoRow label="Zoning" value={selected.pluto_zoning} />
                    <InfoRow label="Units" value={selected.units} />
                  </div>
                </DetailSection>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
